import React, { useMemo, useCallback } from 'react'
import { Alert, Button, Flex, Spin, Typography, Dropdown, Tooltip, Tag } from 'antd'
import { PlusOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { DragDropContext } from 'react-beautiful-dnd'
import {
    useFetchActiveBoostersQuery,
    useFetchQuestionRubricBoostersQuery,
    useAddBoosterToQuestionRubricMutation,
    useRemoveBoosterFromQuestionRubricMutation,
    useUpdateQuestionRubricBoosterMutation,
} from '../../../../../store/apis/booster.api'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import { useAssignmentAccess } from '../../../components/access/Access'
import PrioritySection from './components/PrioritySection'
import { TextContentRules } from '../../../components/access/access-rules/assignment-rules'

const { Title, Text } = Typography

const BoostersList = ({ questionRubricId }) => {
    const { hasAccess: hasEditPermission } = useAssignmentAccess(TextContentRules.EDIT_GRADINGS)

    // Fetch available boosters and question rubric-specific boosters
    const {
        data: activeBoosters = [],
        isLoading: isLoadingActiveBoosters,
        error: activeBoostersError,
    } = useFetchActiveBoostersQuery()

    const {
        data: questionRubricBoosters = [],
        isLoading: isLoadingQuestionRubricBoosters,
        error: questionRubricBoostersError,
    } = useFetchQuestionRubricBoostersQuery(
        { questionRubricId },
        { skip: !questionRubricId }
    )

    // Mutations
    const [addBooster, addBoosterResult] = useAddBoosterToQuestionRubricMutation()
    const [removeBooster, removeBoosterResult] = useRemoveBoosterFromQuestionRubricMutation()
    const [updateBooster] = useUpdateQuestionRubricBoosterMutation()

    // Get available boosters (not already selected)
    const selectedBoosterIds = new Set(questionRubricBoosters.map(qb => qb.senseBoosterId))
    const availableBoosters = activeBoosters.filter(booster => !selectedBoosterIds.has(booster.id))

    // Group tools by priority and get priority list
    const toolsByPriority = useMemo(() => {
        const grouped = {}
        questionRubricBoosters.forEach(tool => {
            const priority = tool.priority || 1
            if (!grouped[priority]) grouped[priority] = []
            grouped[priority].push(tool)
        })
        return grouped
    }, [questionRubricBoosters])

    const priorities = useMemo(() => {
        const existingPriorities = Object.keys(toolsByPriority).map(Number).sort((a, b) => a - b)
        
        if (existingPriorities.length === 0) {
            return [1] // Default to priority 1 if no tools exist
        }
        
        // Create continuous range from 1 to highest priority
        // This ensures empty priorities remain visible for drag-and-drop
        const maxPriority = Math.max(...existingPriorities)
        const continuousPriorities = []
        for (let i = 1; i <= maxPriority; i++) {
            continuousPriorities.push(i)
        }
        
        return continuousPriorities
    }, [toolsByPriority])

    const getNextPriority = useCallback(() => {
        // If no tools exist, start with priority 1
        if (questionRubricBoosters.length === 0) return 1
        return Math.max(...priorities, 0) + 1
    }, [priorities, questionRubricBoosters.length])

    const handleAddBooster = async (boosterId) => {
        try {
            const response = await addBooster({
                questionRubricId,
                senseBoosterId: boosterId,
                priority: getNextPriority(), // Add to highest priority (latest)
            })

            if ('data' in response) {
                openNotificationWithIcon('success', 'Tool added successfully')
            }
        } catch (error) {
            openNotificationWithIcon('error', 'Failed to add tool')
        }
    }

    const handleRemoveBooster = async (boosterId) => {
        try {
            const response = await removeBooster({ 
                questionRubricId,
                boosterId,
            })

            if ('data' in response) {
                openNotificationWithIcon('success', 'Tool removed successfully')
            }
        } catch (error) {
            openNotificationWithIcon('error', 'Failed to remove tool')
        }
    }

    const handleDragEnd = async (result) => {
        const { destination, source, draggableId } = result

        // Dropped outside any droppable area
        if (!destination) return

        // Dropped in the same position
        if (destination.droppableId === source.droppableId && destination.index === source.index) return

        const newPriority = parseInt(destination.droppableId)
        const boosterId = parseInt(draggableId)

        try {
            const response = await updateBooster({
                questionRubricId,
                boosterId,
                priority: newPriority,
            })

            if ('data' in response) {
                openNotificationWithIcon('success', 'Tool priority updated successfully')
            }
        } catch (error) {
            openNotificationWithIcon('error', 'Failed to update tool priority')
        }
    }

    const getBoosterDetails = (senseBoosterId) => {
        return activeBoosters.find(booster => booster.id === senseBoosterId)
    }

    // Create dropdown menu items for available boosters
    const boosterMenuItems = availableBoosters.map(booster => ({
        key: booster.id,
        label: (
            <Flex vertical style={{ maxWidth: 300, padding: '4px 0' }}>
                <Flex gap={8} align='center'>
                    <Text strong>{booster.displayName}</Text>
                    {booster.isInternal && (
                        <Tag size='small' color='blue'>Internal</Tag>
                    )}
                </Flex>
                <Text type='secondary' style={{ fontSize: '12px', marginTop: 2 }}>
                    {booster.description}
                </Text>
            </Flex>
        ),
        onClick: () => handleAddBooster(booster.id),
    }))

    const isLoading = isLoadingActiveBoosters || isLoadingQuestionRubricBoosters
    const hasError = activeBoostersError || questionRubricBoostersError

    const header = (
        <Flex vertical gap={8}>
            <Flex justify='space-between' align='center'>
                <Flex gap={8} align='center'>
                    <Title level={4} style={{ margin: 0, fontSize: '1rem' }}>
                        Tools Enabled
                    </Title>
                    <Tooltip title="Tools in Priority 1 run first and pass their output to Priority 2, and so on. Tools within the same priority run in parallel.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </Flex>
                <Flex gap={8}>
                    {hasEditPermission && availableBoosters.length > 0 && (
                        <Dropdown
                            menu={{ 
                                items: boosterMenuItems,
                                style: { maxHeight: 480, overflowY: 'auto' }
                            }}
                            trigger={['click']}
                            placement='bottomRight'
                            disabled={addBoosterResult.isLoading}
                        >
                            <Button
                                icon={<PlusOutlined aria-hidden='true' />}
                                loading={addBoosterResult.isLoading}
                                type='text'
                            >
                                Add New <DownOutlined />
                            </Button>
                        </Dropdown>
                    )}
                </Flex>
            </Flex>
            {hasError && (
                <Alert 
                    message={activeBoostersError || questionRubricBoostersError} 
                    type='error' 
                    showIcon 
                    closable 
                />
            )}
        </Flex>
    )

    // Add empty priority if + Priority was clicked or show next available
    const displayPriorities = useMemo(() => {
        const allPriorities = [...priorities]
        // Always show next available priority for empty state
        if (questionRubricBoosters.length > 0) {
            allPriorities.push(getNextPriority())
        }
        return allPriorities
    }, [priorities, questionRubricBoosters.length, getNextPriority])

    const renderPrioritySection = (priority) => {
        const tools = toolsByPriority[priority] || []
        
        return (
            <PrioritySection
                key={priority}
                priority={priority}
                tools={tools}
                getBoosterDetails={getBoosterDetails}
                onRemoveTool={handleRemoveBooster}
                isRemoving={removeBoosterResult.isLoading}
            />
        )
    }

    const content = () => {
        if (questionRubricBoosters.length === 0) {
            return (
                <Flex justify='center' style={{ padding: '20px 0' }}>
                    <Text type='secondary'>No tools selected</Text>
                </Flex>
            )
        }

        return (
            <DragDropContext onDragEnd={handleDragEnd}>
                {displayPriorities.map(renderPrioritySection)}
            </DragDropContext>
        )
    }

    return (
        <Flex vertical gap={12}>
            {header}
            <Spin spinning={isLoading} tip='Loading tools...'>
                {content()}
            </Spin>
        </Flex>
    )
}

export default BoostersList
